import dayjs from "dayjs";
import { FC, Ref, useCallback, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { FunctionCoach, ProgramTopic } from "../../api/services/program/models";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import { UserDetails } from "../../helpers/types/common";
import {
  isNotEmpty,
  setTopicApiDataToDropDownOption,
} from "../../helpers/utils";
import { useMultiSelectARIALabels } from "../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  convertFunctionsOrCoachToDropdown,
  getCalculatedDefaultDuration,
} from "../../pages/createProgram/helper";
import { AddActivityFormKey } from "../../pages/createProgram/modelsAndSchemas/Models";
import { activityTimelineSiteCoreKeys } from "../../pages/createProgram/siteCore/SiteCoreKeys";
import { programCreatedDateSelector } from "../../redux/slices/program/programSelector";
import { MuiDateFieldFormController } from "../dateField/MuiDateFieldFormController";
import { FormController } from "../formController/FormController";
import { MuiGrid } from "../grid/MuiGrid";
import { MuiMultiSelect } from "../multiSelect/MuiMultiSelect";
import { MuiSelect, OptionsDataType } from "../select/MuiSelect";
import { MuiTextField } from "../textField/MuiTextfield";
import { ActivityFormWrapper } from "./ActivityFrom.style";

const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

interface ActivityFormProps {
  pageId: string;
  filteredActivityTypes: OptionsDataType[];
  filteredModality: OptionsDataType[];
  topicListData: ProgramTopic[];
  handleActivityTypeChange: (value: string) => void;
  handleModalityChange: (value: string) => void;
  programCoachList: FunctionCoach[] | UserDetails[];
  topicRef: Ref<{ resetValues: () => void }>;
  isBulkAdd?: boolean;
  siteCoreKeys?: {
    [key: string]: string;
  };
  isFromNISParticipant?: boolean;
}

export const ActivityForm: FC<ActivityFormProps> = ({
  pageId,
  filteredActivityTypes,
  filteredModality,
  topicListData,
  programCoachList,
  handleActivityTypeChange,
  handleModalityChange,
  topicRef,
  isBulkAdd = true,
  siteCoreKeys = activityTimelineSiteCoreKeys,
  isFromNISParticipant,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchKeyByValueOfDropdown,
  } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const programCreatedDate = useSelector(programCreatedDateSelector);
  const minDate = dayjs(programCreatedDate)
    .subtract(6, "months") // upto 6 months back from program created date
    .startOf("day");

  const { commonARIALabel } = useNavigationSiteCore();
  const {
    control,
    getValues,
    setValue,
    formState: { touchedFields },
  } = useFormContext();

  const getLabel = useCallback(
    (key: string) => {
      return fetchLabel(siteCoreKeys.activityTimeline, key);
    },
    [fetchLabel, siteCoreKeys.activityTimeline],
  );

  const getDropdownValues = useCallback(
    (key: string) => {
      return fetchDropdownValues(siteCoreKeys.activityTimeline, key);
    },
    [fetchDropdownValues, siteCoreKeys.activityTimeline],
  );

  const topic = useWatch({
    name: AddActivityFormKey.topic,
    control,
    defaultValue: [],
  });

  const calculatedDefaultDuration = useMemo(() => {
    return getCalculatedDefaultDuration(topic, topicListData ?? []);
  }, [topic, topicListData]);

  useEffect(() => {
    if (calculatedDefaultDuration && touchedFields?.topic) {
      const key = fetchKeyByValueOfDropdown(
        siteCoreKeys.activityTimeline,
        siteCoreKeys.actualDurationMinutesList,
        String(calculatedDefaultDuration),
      );
      setValue(
        AddActivityFormKey.defaultDuration,
        String(calculatedDefaultDuration),
      );
      if (key) {
        setValue(AddActivityFormKey.actualDurationInMinutes, key);
      }
    }
  }, [calculatedDefaultDuration]);

  const coachesDropdownOptions = useMemo(() => {
    if (programCoachList?.length) {
      return convertFunctionsOrCoachToDropdown(programCoachList);
    }
    return [];
  }, [programCoachList]);
  const maxDate = dayjs();

  return (
    <ActivityFormWrapper>
      <MuiGrid container>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={AddActivityFormKey.activityType}
          >
            <MuiSelect
              label={getLabel(siteCoreKeys.activityType)}
              options={filteredActivityTypes}
              visibleDefaultSelectOption
              showSearch
              id={`${pageId}-${AddActivityFormKey.activityType}`}
              placeholder={commonLabels.selectPlaceHolder}
              onChange={(e) =>
                handleActivityTypeChange(e.target.value as string)
              }
              isMandatory
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={AddActivityFormKey.modality}>
            <MuiSelect
              label={getLabel(siteCoreKeys.modality)}
              options={filteredModality}
              visibleDefaultSelectOption
              showSearch
              id={`${pageId}-${AddActivityFormKey.modality}`}
              placeholder={commonLabels.selectPlaceHolder}
              isMandatory
              onChange={(e) => handleModalityChange(e.target.value as string)}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={AddActivityFormKey.topic}
            ref={topicRef}
          >
            <MuiMultiSelect
              label={getLabel(siteCoreKeys.topic)}
              options={setTopicApiDataToDropDownOption(topicListData)}
              showSearch
              showChip
              isMandatory
              showSelectAllOption
              id={`${pageId}-${AddActivityFormKey.topic}`}
              chipId={`${pageId}-${AddActivityFormKey.topic}-chip`}
              dialogTitle={getLabel(siteCoreKeys.topic)} //change label if required
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>
        {/* default duration */}
        <MuiGrid size={gridSize}>
          <MuiTextField
            id={`${pageId}-defaultDuration`}
            disabled
            value={calculatedDefaultDuration}
            label={getLabel(siteCoreKeys.defaultDurationMinutes)}
            requiredARIALabel={commonARIALabel.required}
          />
        </MuiGrid>
        {/* actual duration minutes field */}
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={AddActivityFormKey.actualDurationInMinutes}
          >
            <MuiSelect
              label={getLabel(siteCoreKeys.actualDurationMinutes)}
              options={getDropdownValues(
                siteCoreKeys.actualDurationMinutesList,
              )}
              visibleDefaultSelectOption
              id={`${pageId}-${AddActivityFormKey.actualDurationInMinutes}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize}>
          <MuiDateFieldFormController
            control={control}
            isMandatory
            name={AddActivityFormKey.activityDate}
            label={getLabel(siteCoreKeys.activityDate)}
            valueFormat={userCountryDateFormat}
            format={userCountryDateFormat}
            className="date-picker"
            maxDate={maxDate}
            minDate={isBulkAdd ? undefined : minDate}
            id={`${pageId}-${AddActivityFormKey.activityDate}`}
            disableManualInput
            value={
              isNotEmpty(getValues(AddActivityFormKey.activityDate))
                ? dayjs(
                    getValues(AddActivityFormKey.activityDate),
                    userCountryDateFormat,
                  )
                : null
            }
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
          />
        </MuiGrid>

        {/* on behalf of coach field*/}
        {!isFromNISParticipant && (
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={AddActivityFormKey.onBehalfOfCoachId}
            >
              <MuiSelect
                label={getLabel(siteCoreKeys.onBehalfOfCoachId)}
                options={coachesDropdownOptions}
                showSearch
                visibleDefaultSelectOption
                id={`${pageId}-${AddActivityFormKey.onBehalfOfCoachId}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}
      </MuiGrid>
      <MuiGrid container>
        <MuiGrid size={{ md: 8, sm: 8, xs: 12 }} className="notes">
          <FormController control={control} name={AddActivityFormKey.notes}>
            <MuiTextField
              id={`${pageId}-${AddActivityFormKey.notes}`}
              label={commonLabels.notes}
              placeholder={commonLabels.inputPlaceHolder}
              isTextarea
              multiline
              rows={4}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </ActivityFormWrapper>
  );
};
